import React, { useState } from 'react';
import { Box, Typography, Alert } from '@mui/material';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import { useTranslation } from 'react-i18next';
import { UserProfile } from '../../types/UserProfile';
import { SettingToggleCard, SettingRadioGroup } from '../common/SettingCard';

interface PrivacySettingsFormProps {
  profile: UserProfile;
  onSave: (updatedProfile: UserProfile) => Promise<void>;
}

const PrivacySettingsForm: React.FC<PrivacySettingsFormProps> = ({ profile, onSave }) => {
  const { t } = useTranslation('settings');
  const [agePrivacy, setAgePrivacy] = useState(profile.age_privacy.toString());
  const [isAnonymousLike, setIsAnonymousLike] = useState(profile.personalize?.p?.lk ?? false);
  const [error, setError] = useState<string | null>(null);

  // トグル/ラジオは選んだ瞬間に保存（楽観更新＋失敗時ロールバック）。
  // 値はサーバー権威（DB）で判定される挙動なので、書き込み完了＝即有効。
  const persist = (next: { age?: string; lk?: boolean }) => {
    const newAge = next.age ?? agePrivacy;
    const newLk = next.lk ?? isAnonymousLike;
    const updatedProfile: UserProfile = {
      ...profile,
      age_privacy: parseInt(newAge),
      personalize: {
        ...profile.personalize,
        p: { ...profile.personalize?.p, lk: newLk },
      },
    };
    setError(null);
    onSave(updatedProfile).catch((e) => {
      console.error('プライバシー設定の更新に失敗しました', e);
      // ロールバック（最後に保存済みの値＝profileへ戻す）
      setAgePrivacy(profile.age_privacy.toString());
      setIsAnonymousLike(profile.personalize?.p?.lk ?? false);
      setError(t('privacy.saveFailed'));
    });
  };

  const handleAgeChange = (v: string) => {
    setAgePrivacy(v);
    persist({ age: v });
  };

  const handleLikeToggle = (on: boolean) => {
    setIsAnonymousLike(on);
    persist({ lk: on });
  };

  return (
    <Box sx={{ width: '100%', maxWidth: 600 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Typography variant="h6" gutterBottom>
        {t('privacy.ageTitle')}
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        {t('privacy.ageDesc')}
      </Typography>
      <Box sx={{ mb: 3 }}>
        <SettingRadioGroup
          name="age-privacy"
          value={agePrivacy}
          onChange={handleAgeChange}
          options={[
            { value: '2', label: t('privacy.ageDetailLabel'), description: t('privacy.ageDetailDesc') },
            { value: '1', label: t('privacy.ageDefaultLabel'), description: t('privacy.ageDefaultDesc') },
            { value: '0', label: t('privacy.ageNoneLabel'), description: t('privacy.ageNoneDesc') },
          ]}
        />
      </Box>

      <Typography variant="h6" gutterBottom>
        {t('privacy.likeTitle')}
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        {t('privacy.likeDesc')}
      </Typography>
      <SettingToggleCard
        icon={<FavoriteBorderIcon sx={{ fontSize: 20 }} />}
        title={t('privacy.likeAnonTitle')}
        description={t('privacy.likeAnonDesc')}
        checked={isAnonymousLike}
        onToggle={handleLikeToggle}
      />
    </Box>
  );
};

export default PrivacySettingsForm;
